/**
 * Simple global notification service to trigger UI notifications
 * from anywhere in the application (including non-React files like api.ts)
 */

export type NotificationType = 'success' | 'error' | 'warning' | 'info';

export interface Notification {
  id: string;
  message: string;
  type: NotificationType;
  duration?: number;
}

type NotificationCallback = (notification: Notification | null) => void;

let currentNotification: Notification | null = null;
const listeners: Set<NotificationCallback> = new Set();

const notifyListeners = () => {
  listeners.forEach((listener) => listener(currentNotification));
};

export const notificationService = {
  /**
   * Show a notification
   */
  show: (message: string, type: NotificationType = 'info', duration: number = 5000) => {
    const id = Math.random().toString(36).substring(2, 9);
    currentNotification = { id, message, type, duration };
    notifyListeners();

    if (duration > 0) {
      setTimeout(() => {
        if (currentNotification?.id === id) {
          notificationService.hide();
        }
      }, duration);
    }
  },

  /**
   * Hide the current notification
   */
  hide: () => {
    currentNotification = null;
    notifyListeners();
  },

  /**
   * Subscribe to notification changes
   */
  subscribe: (callback: NotificationCallback) => {
    listeners.add(callback);
    callback(currentNotification);
    return () => {
      listeners.delete(callback);
    };
  },

  /**
   * Shortcut for error notifications
   */
  error: (message: string, duration?: number) => {
    notificationService.show(message, 'error', duration);
  },

  /**
   * Shortcut for success notifications
   */
  success: (message: string, duration?: number) => {
    notificationService.show(message, 'success', duration);
  },

  /**
   * Shortcut for warning notifications
   */
  warning: (message: string, duration?: number) => {
    notificationService.show(message, 'warning', duration);
  },
};
